import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLatestVersion } from '../data/changelogData';
import './Documentation.css';

function Documentation() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const activePage = searchParams.get('page') || 'get-started';
  const version = useLatestVersion();

  const [activeSection, setActiveSection] = useState('top');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const docPages = [
    { id: 'get-started', title: 'Getting Started', category: 'INTRODUCTION', icon: 'fa-rocket' },
    { id: 'architecture', title: 'Neural Architecture', category: 'INTRODUCTION', icon: 'fa-sitemap' },
    { id: 'theming', title: 'Theming System', category: 'INTRODUCTION', icon: 'fa-palette' },
    { id: 'ai', title: 'DAIV-2000 Intelligence', category: 'INTRODUCTION', icon: 'fa-wand-magic-sparkles' },
    { id: 'notifications', title: 'Notification Center', category: 'INTRODUCTION', icon: 'fa-bell' },
    { id: 'governance', title: 'Platform Governance', category: 'INTRODUCTION', icon: 'fa-shield-halved' },
    { id: 'dash-overview', title: 'Dashboard v4.0', category: 'CORE FEATURES', icon: 'fa-chalkboard-user' },
    { id: 'resume-4', title: 'Resume 4.0 Standard', category: 'CORE FEATURES', icon: 'fa-file-pdf' },
    { id: 'verification', title: 'Neural Verification', category: 'CORE FEATURES', icon: 'fa-fingerprint' },
    { id: 'linking', title: 'Linking Identifiers', category: 'CORE FEATURES', icon: 'fa-link' },
    { id: 'embeds', title: 'Embed Widgets', category: 'CORE FEATURES', icon: 'fa-code' },
    { id: 'portfolios', title: 'Public Portfolios', category: 'CORE FEATURES', icon: 'fa-briefcase' },
    { id: 'github', title: 'GitHub APIs', category: 'INTEGRATIONS', icon: 'fa-brands fa-github' },
    { id: 'leetcode', title: 'LeetCode Sync', category: 'INTEGRATIONS', icon: 'fa-code' },
    { id: 'codeforces', title: 'Codeforces Arena', category: 'INTEGRATIONS', icon: 'fa-chart-simple' }
  ];

  const filteredPages = docPages.filter(p =>
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    setActiveSection('top');
    window.scrollTo(0, 0);
  }, [activePage]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - 100, behavior: 'smooth' });
    }
    setActiveSection(id);
    setMobileMenuOpen(false);
  };

  const setPage = (pageName) => {
    navigate(`/docs?page=${pageName}`);
    setMobileMenuOpen(false);
  };

  // --- CONTENT RENDERERS ---

  const renderGetStarted = () => (
    <>
      <div className="docs-breadcrumbs">
        <span>Docs</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Intro</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Getting Started</span>
      </div>
      <h1 className="docs-title">Getting Started</h1>
      <p className="docs-lead">Harness the power of the v4.0.0.x baseline to consolidate your technical identity into a single, high-fidelity professional footprint.</p>
      <hr className="docs-divider" />

      <section id="top" className="docs-section">
        <h2>1. Establish Your Identity</h2>
        <p>Your DevTracker presence begins with a unified <strong>SUID (System User Identifier)</strong>. This hash is immutable and serves as your primary key for platform oversight.</p>
        <ol>
          <li>Navigate to <Link to="/register" className="docs-link">Authentication</Link>.</li>
          <li>Upon registration, an <strong>AID</strong> (Administrative Identity) or <strong>SUID</strong> is generated.</li>
          <li>Your profile is immediately reachable via <code>/dev/:profileId</code>.</li>
        </ol>
      </section>

      <section id="config" className="docs-section">
        <h2>2. Data Node Linkage</h2>
        <p>DevTracker V4 utilizes <strong>DAIV-2000</strong> archaeological parsing to ingest metadata from your public technical nodes.</p>
        <div className="docs-note info">
          <i className="fa-solid fa-circle-info"></i>
          <div>
            <strong>Identity Security:</strong> We only track public data. No passwords or private tokens are EVER stored within the Origin Core network.
          </div>
        </div>
      </section>
    </>
  );

  const renderArchitecture = () => (
    <>
      <div className="docs-breadcrumbs">
        <span>Docs</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Intro</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Neural Architecture</span>
      </div>
      <h1 className="docs-title" id="top">Neural Architecture</h1>
      <p className="docs-lead">DevTracker operates on a high-synchronicity serverless framework designed for millisecond-latency data delivery.</p>
      <hr className="docs-divider" />

      <section className="docs-section">
        <h2>Origin Core v4.0</h2>
        <p>Our architecture utilizes <strong>Realtime State Injection</strong> to ensure your dashboard reflecting your latest technical metrics without manual refreshing.</p>
        <ul className="docs-list">
          <li><strong>Firebase Governance:</strong> Real-time storage of identifiers and metadata nodes.</li>
          <li><strong>Archaeological Ingestion:</strong> Parallel API proxies that scrape public metrics from GitHub, LeetCode, and Codeforces.</li>
          <li><strong>Neural Output Layer:</strong> High-fidelity React components that convert disparate JSON blobs into professional resumes.</li>
        </ul>
      </section>
    </>
  );

  const renderAI = () => (
    <>
      <div className="docs-breadcrumbs"><span>Docs</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Intro</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">DAIV-2000 Intelligence</span></div>
      <h1 className="docs-title" id="top">DAIV-2000 Intelligence</h1>
      <p className="docs-lead">The Director of Artificial Intelligence & Verification (DAIV-2000) is the platform's central logic engine.</p>
      <hr className="docs-divider" />
      <section id="dna" className="docs-section">
        <h2>Neural DNA Scoring</h2>
        <p>The engine analyzes your technical footprint to calculate a <strong>DNA Score</strong> (0-100). This isn't just a count of commits; it's a multi-dimensional analysis of problem-solving depth and consistency.</p>
        <div className="docs-note info">
          <i className="fa-solid fa-brain"></i>
          <div><strong>Archetype Mapping:</strong> DAIV-2000 assigns a professional persona (e.g., "Complexity Crusader") based on your algorithmic trajectory.</div>
        </div>
      </section>
    </>
  );

  const renderResume4 = () => (
    <>
      <div className="docs-breadcrumbs"><span>Docs</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Core Features</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Resume 4.0 Standard</span></div>
      <h1 className="docs-title" id="top">Resume 4.0 Standard</h1>
      <p className="docs-lead">Experience the "Traditional Professional" evolution—a high-fidelity document layout designed for maximum vocational impact.</p>
      <hr className="docs-divider" />
      <section className="docs-section">
        <h2>Traditional Professional Aesthetic</h2>
        <p>Resume 4.0 pivots away from futuristic gradients to a clean, authoritative <strong>White Paper</strong> design with sharp typography and clear section borders.</p>
        <ul className="docs-list">
          <li><strong>Dual Column Grid:</strong> Focused contact info on the right, expansive technical project history on the left.</li>
          <li><strong>Neural QR-Link:</strong> Every resume includes an authenticated QR code linked to the Verification Portal.</li>
          <li><strong>Print Performance:</strong> Optimized via <code>@media print</code> for perfect A4 document generation.</li>
        </ul>
      </section>
    </>
  );

  const renderVerification = () => (
    <>
      <div className="docs-breadcrumbs"><span>Docs</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Core Features</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Neural Verification</span></div>
      <h1 className="docs-title" id="top">Neural Verification</h1>
      <p className="docs-lead">DevTracker identities are protected by a mandatory 15-second security audit cycle.</p>
      <hr className="docs-divider" />
      <section className="docs-section">
        <h2>The 15-Second Audit</h2>
        <p>When a visitor scans your resume QR code, they are directed to a specialized <strong>Verification Portal</strong>. The DAIV engine performs a neural audit to ensure the SUID is authentic.</p>
        <div className="docs-note warning">
          <i className="fa-solid fa-shield-halved"></i>
          <div><strong>Status Integrity:</strong> If a profile is suspended or restricted, the verification cycle will fail, providing real-time data protection for recruiters.</div>
        </div>
      </section>
    </>
  );

  const renderGovernance = () => (
    <>
      <div className="docs-breadcrumbs"><span>Docs</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Intro</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Platform Governance</span></div>
      <h1 className="docs-title" id="top">Platform Governance</h1>
      <p className="docs-lead">Legal and operational standards established to maintain technical sovereignty within the DevTracker ecosystem.</p>
      <hr className="docs-divider" />
      <section className="docs-section">
        <h2>Legal Governance v4.0.0.x</h2>
        <p>Our Privacy Protocols and Operational Terms utilize a premium <strong>Corporate Governance</strong> document layout. This standard prioritizes clarity, authority, and cryptographic integrity.</p>
        <div className="docs-note info">
          <i className="fa-solid fa-shield-halved"></i>
          <div><strong>Neural Authority:</strong> Every legal node is signed with a 16-character digital authenticity code, ensuring you are reviewing the official platform standard.</div>
        </div>
        <p>Key highlights of the v4 governance upgrade include:</p>
        <ul className="docs-list">
          <li><strong>Serif Document Aesthetic:</strong> Utilizing the 'Source Serif 4' typography for maximum legibility in complex clauses.</li>
          <li><strong>Clause Tracking:</strong> Direct linking to specific legal paragraphs (e.g., #clause-3) for administrative precision.</li>
          <li><strong>Data Sovereignty:</strong> Explicit documentation of the DAIV-2000 archaeological ingestion boundaries.</li>
        </ul>
      </section>
    </>
  );

  const renderDashOverview = () => (
    <>
      <div className="docs-breadcrumbs"><span>Docs</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Core Features</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Dashboard v4.0</span></div>
      <h1 className="docs-title" id="top">Dashboard v4.0</h1>
      <p className="docs-lead">A unified technical workspace designed for "Traditional Professional" configuration and identity management.</p>
      <hr className="docs-divider" />
      <section className="docs-section">
        <h2>Modern Workspace Modules</h2>
        <div className="docs-platforms-grid">
          <div className="glass-card docs-platform-card">
            <i className="fa-solid fa-user-tie"></i>
            <h4>Dossier Settings</h4>
            <p>Manage LinkedIn, Phone, and professional summaries.</p>
          </div>
          <div className="glass-card docs-platform-card leetcode">
            <i className="fa-solid fa-fingerprint"></i>
            <h4>Neural ID</h4>
            <p>Monitor SUID and audit your verified metrics.</p>
          </div>
          <div className="glass-card docs-platform-card">
            <i className="fa-solid fa-file-invoice"></i>
            <h4>Case Tracking</h4>
            <p>Real-time status updates on support and identity appeals.</p>
          </div>
        </div>
      </section>
    </>
  );

  const renderEmbeds = () => (
    <>
      <div className="docs-breadcrumbs"><span>Docs</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Core Features</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Embed Widgets</span></div>
      <h1 className="docs-title" id="top">Embed Widgets</h1>
      <p className="docs-lead">Inject your live technical footprint into any third-party environment with pixel-perfect resolution.</p>
      <hr className="docs-divider" />
      <section id="deploy" className="docs-section">
        <h2>Deploy Embed Widgets</h2>
        <p>In your Dashboard settings, generate a specialized iframe link targeting your <strong>EID (Embed Identifier)</strong>.</p>
        <div className="docs-code-block glass-card">
          <div className="code-header"><span className="code-lang">HTML</span></div>
          <pre><code><span className="tag">&lt;iframe</span>
            <span className="attr">src</span>=<span className="string">"https://devtracker.site/embed/EID-DVT-XXXXXX"</span>
            <span className="attr">width</span>=<span className="string">"100%"</span>
            <span className="attr">height</span>=<span className="string">"600"</span>
            <span className="attr">frameBorder</span>=<span className="string">"0"</span><span className="tag">&gt;</span>
            <span className="tag">&lt;/iframe&gt;</span></code></pre>
        </div>
      </section>
    </>
  );

  return (
    <div className="docs-page">
      <div className="docs-mobile-header">
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="docs-menu-toggle">
          <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'}`}></i> {mobileMenuOpen ? 'Close Navigator' : 'Technical Navigator'}
        </button>
      </div>

      <div className="docs-container">
        <aside className={`docs-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="docs-search-mock glass-card" onClick={() => setSearchOpen(true)}>
            <i className="fa-solid fa-magnifying-glass"></i>
            <span>Search Documentation {version}...</span>
            <kbd>Ctrl K</kbd>
          </div>

          <nav className="docs-nav">
            <div className="docs-nav-group">
              <h4 className="docs-nav-title">INTRODUCTION</h4>
              <ul className="docs-nav-list">
                {docPages.filter(p => p.category === 'INTRODUCTION').map(page => (
                  <li key={page.id} className={activePage === page.id ? 'active' : ''}>
                    <button onClick={() => setPage(page.id)}>
                      <i className={`fa-solid ${page.icon}`}></i> {page.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="docs-nav-group">
              <h4 className="docs-nav-title">CORE FEATURES</h4>
              <ul className="docs-nav-list">
                {docPages.filter(p => p.category === 'CORE FEATURES').map(page => (
                  <li key={page.id} className={activePage === page.id ? 'active' : ''}>
                    <button onClick={() => setPage(page.id)}>
                      <i className={`fa-solid ${page.icon}`}></i> {page.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="docs-nav-group">
              <h4 className="docs-nav-title">INTEGRATIONS</h4>
              <ul className="docs-nav-list">
                {docPages.filter(p => p.category === 'INTEGRATIONS').map(page => (
                  <li key={page.id} className={activePage === page.id ? 'active' : ''}>
                    <button onClick={() => setPage(page.id)}>
                      <i className={`fa-solid ${page.icon}`}></i> {page.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
        </aside>

        <main className="docs-content animate-up">
          {activePage === 'get-started' && renderGetStarted()}
          {activePage === 'architecture' && renderArchitecture()}
          {activePage === 'theming' && (
            <>
              <div className="docs-breadcrumbs"><span>Docs</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Intro</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Theming System</span></div>
              <h1 className="docs-title" id="top">Theming System</h1>
              <p className="docs-lead">DevTracker v4.0 introduces the "Traditional Professional" light-paper aesthetic—balancing high-fidelity detail with document-level authority.</p>
              <hr className="docs-divider" />
              <section className="docs-section">
                <h2>Adaptive Theming Engine</h2>
                <p>The system utilizes <strong>CSS Custom Properties</strong> to inject theme tokens dynamically across the document tree. The primary v4 baseline is optimized for Light-Paper renders, specifically for recruitment performance.</p>
                <div className="docs-platforms-grid">
                  <div className="glass-card docs-platform-card">
                    <i className="fa-solid fa-sun text-accent"></i>
                    <h4>Light Mode (v4 Standard)</h4>
                    <p>Designed for A4 print parity, high-contrast readability, and traditional professional aesthetics.</p>
                  </div>
                  <div className="glass-card docs-platform-card">
                    <i className="fa-solid fa-moon"></i>
                    <h4>Dark Mode (Classic)</h4>
                    <p>Optimized for low-light environment developer workflows and administrator command centers.</p>
                  </div>
                </div>
              </section>
            </>
          )}
          {activePage === 'ai' && renderAI()}
          {activePage === 'notifications' && (
            <>
              <div className="docs-breadcrumbs"><span>Docs</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Intro</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Notification Center</span></div>
              <h1 className="docs-title" id="top">Notification Protocols</h1>
              <p className="docs-lead">DevTracker utilizes a multi-tier, real-time notification engine to broadcast architectural updates and account triggers.</p>
              <hr className="docs-divider" />
              <section className="docs-section">
                <h2>Message Taxonomy</h2>
                <div className="docs-platforms-grid">
                  <div className="glass-card docs-platform-card border-success">
                    <i className="fa-solid fa-circle-check text-success"></i>
                    <h4>Success</h4>
                    <p>Triggered upon atomic database operations (e.g., Profile ID updates).</p>
                  </div>
                  <div className="glass-card docs-platform-card border-warning">
                    <i className="fa-solid fa-triangle-exclamation text-warning"></i>
                    <h4>Warning</h4>
                    <p>Alerts regarding missing integration handles or pending audits.</p>
                  </div>
                </div>
                <div className="docs-note info">
                  <i className="fa-solid fa-bell"></i>
                  <div><strong>Live Signals:</strong> All messages are synchronized via Firebase <code>onValue</code> listeners for zero-latency awareness.</div>
                </div>
              </section>
            </>
          )}
          {activePage === 'governance' && renderGovernance()}
          {activePage === 'dash-overview' && renderDashOverview()}
          {activePage === 'resume-4' && renderResume4()}
          {activePage === 'verification' && renderVerification()}
          {activePage === 'linking' && (
            <>
              <div className="docs-breadcrumbs"><span>Docs</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Core Features</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Linking Identifiers</span></div>
              <h1 className="docs-title" id="top">Linking Identifiers</h1>
              <p className="docs-lead">DevTracker utilizes a multi-tier identification hierarchy to balance public transparency with deep systemic security.</p>
              <hr className="docs-divider" />
              
              <section className="docs-section">
                <h2>The 5-Tier Identity Architecture</h2>
                <div className="docs-platforms-grid">
                  <div className="glass-card docs-platform-card">
                    <i className="fa-solid fa-microchip text-accent"></i>
                    <h4>SUID (System)</h4>
                    <p>The primary internal key. Used for administrative triage and private support inquiries.</p>
                  </div>
                  <div className="glass-card docs-platform-card">
                    <i className="fa-solid fa-fingerprint"></i>
                    <h4>UID (Public)</h4>
                    <p>Standard hashed identifier for public profiles and social discovery (e.g., <code>UID-DVT-XXXXXX</code>).</p>
                  </div>
                  <div className="glass-card docs-platform-card">
                    <i className="fa-solid fa-link"></i>
                    <h4>EID (Embed)</h4>
                    <p>Stateless identifier specifically for third-party widget ingestion and cross-origin security.</p>
                  </div>
                </div>

                <div className="docs-platforms-grid" style={{marginTop: '20px'}}>
                  <div className="glass-card docs-platform-card">
                    <i className="fa-solid fa-user-tag text-success"></i>
                    <h4>PID (Custom)</h4>
                    <p>The user-defined Profile ID utilized for SEO-optimized vanity URLs (e.g., <code>/dev/johndoe</code>).</p>
                  </div>
                  <div className="glass-card docs-platform-card">
                    <i className="fa-solid fa-shield-halved"></i>
                    <h4>SID (Session)</h4>
                    <p>Ephemeral, high-entropy token generated at login to manage the stateless authentication lifecycle.</p>
                  </div>
                </div>
              </section>

              <section className="docs-section">
                <h2>Security Invariants</h2>
                <div className="docs-note info">
                  <i className="fa-solid fa-shield-lock"></i>
                  <div><strong>Zero-Knowledge Linking:</strong> Public UIDs are algorithmically decoupled from SUIDs. Compromising a public ID does not grant administrative access to system-level data nodes.</div>
                </div>
              </section>
            </>
          )}
          {activePage === 'embeds' && renderEmbeds()}
          {activePage === 'portfolios' && (
            <>
              <div className="docs-breadcrumbs"><span>Docs</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Core Features</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Public Portfolios</span></div>
              <h1 className="docs-title" id="top">Public Portfolios</h1>
              <p className="docs-lead">The <code>/dev/:profileId</code> ecosystem provides a high-fidelity, SEO-optimized landing page for every developer.</p>
              <hr className="docs-divider" />
              <section className="docs-section">
                <h2>Traditional Professional Standard</h2>
                <p>Version 4.0 introduces the <strong>Resume 4.0</strong> render engine, which maps technical data into a dual-column Reference Standard layout.</p>
                <div className="docs-note tip">
                  <i className="fa-solid fa-wand-magic-sparkles"></i>
                  <div><strong>Showcase Mode:</strong> Use <code>/resume/demo</code> to preview the high-fidelity professional layout with sample datasets.</div>
                </div>
              </section>
            </>
          )}
          {activePage === 'github' && (
            <>
              <div className="docs-breadcrumbs"><span>Docs</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Integrations</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">GitHub APIs</span></div>
              <h1 className="docs-title" id="top">GitHub API Ingestion</h1>
              <p className="docs-lead">DevTracker utilizes the GitHub REST & GraphQL APIs to calculate deep-impact metrics and repository sentiment.</p>
              <hr className="docs-divider" />
              <section className="docs-section">
                <h2>Security Protocols</h2>
                <p>We utilize the <strong>Fine-grained Personal Access Token</strong> standard for secure data ingestion. DevTracker never requests sensitive scopes (e.g., repository delete/invite access).</p>
                <div className="docs-code-block glass-card">
                  <div className="code-header"><span className="code-lang">API Request</span></div>
                  <pre><code><span className="tag">GET</span> <span className="string">/users/:username/repos?sort=updated</span></code></pre>
                </div>
              </section>
            </>
          )}
          {activePage === 'leetcode' && (
            <>
              <div className="docs-breadcrumbs"><span>Docs</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Integrations</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">LeetCode Sync</span></div>
              <h1 className="docs-title" id="top">LeetCode Intelligence</h1>
              <p className="docs-lead">DevTracker synchronizes with LeetCode to calculate problem-solving velocity and difficulty distribution.</p>
              <hr className="docs-divider" />
              <section className="docs-section">
                <h2>Data Ingestion Logic</h2>
                <p>The system tracks <strong>Easy, Medium, and Hard</strong> solve counts to derive the "Complex Crusader" archetype score within the DAIV-2000 engine.</p>
                <div className="docs-code-block glass-card">
                  <div className="code-header"><span className="code-lang">Data Mapping</span></div>
                  <pre><code><span className="string">"solveCount"</span>: <span className="tag">&#123; "hard": 42, "medium": 120, "easy": 250 &#125;</span></code></pre>
                </div>
              </section>
            </>
          )}
          {activePage === 'codeforces' && (
            <>
              <div className="docs-breadcrumbs"><span>Docs</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Integrations</span> <i className="fa-solid fa-chevron-right"></i> <span className="current">Codeforces Arena</span></div>
              <h1 className="docs-title" id="top">Codeforces Arena Audit</h1>
              <p className="docs-lead">Real-time rank synchronization and rating trajectory analysis for competitive programming profiles.</p>
              <hr className="docs-divider" />
              <section className="docs-section">
                <h2>Rank Tiers</h2>
                <p>DevTracker maps Codeforces ranks (e.g., Candidate Master, Grandmaster) into high-fidelity visual badges displayed on the primary dossier.</p>
              </section>
            </>
          )}
        </main>

        <aside className="docs-toc">
          <div className="toc-sticky">
            <h4>On this page</h4>
            <ul>
              <li className={activeSection === 'top' ? 'active' : ''}>
                <button onClick={() => scrollToSection('top')}>Top</button>
              </li>
              {activePage === 'get-started' && (
                <li className={activeSection === 'config' ? 'active' : ''}>
                  <button onClick={() => scrollToSection('config')}>Data Node Linkage</button>
                </li>
              )}
              {activePage === 'ai' && (
                <li className={activeSection === 'dna' ? 'active' : ''}>
                  <button onClick={() => scrollToSection('dna')}>Neural DNA Scoring</button>
                </li>
              )}
              {activePage === 'embeds' && (
                <li className={activeSection === 'deploy' ? 'active' : ''}>
                  <button onClick={() => scrollToSection('deploy')}>Deploy Widgets</button>
                </li>
              )}
            </ul>
          </div>
        </aside>
      </div>

      {searchOpen && (
        <div className="docs-search-modal-overlay" onClick={() => setSearchOpen(false)}>
          <div className="docs-search-modal glass-card animate-up" onClick={e => e.stopPropagation()}>
            <div className="search-modal-header">
              <i className="fa-solid fa-magnifying-glass"></i>
              <input
                type="text"
                placeholder="Search technical nodes..."
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button onClick={() => setSearchOpen(false)}><i className="fa-solid fa-xmark"></i></button>
            </div>

            <div className="search-modal-results">
              {filteredPages.length > 0 ? (
                filteredPages.map(page => (
                  <div key={page.id} className="search-result-item" onClick={() => { setPage(page.id); setSearchOpen(false); }}>
                    <div className="result-icon">
                      <i className={`fa-solid ${page.icon}`}></i>
                    </div>
                    <div className="result-info">
                      <div className="result-title">{page.title}</div>
                      <div className="result-path">{page.category}</div>
                    </div>
                    <i className="fa-solid fa-chevron-right result-arrow"></i>
                  </div>
                ))
              ) : (
                <div className="search-no-results">
                  <p>No identifiers found for "{searchQuery}"</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Documentation;
